import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { InvoiceService } from '@api/services/invoice.service';
import {
    ListB2BInvoiceStatusResponse,
} from '@api/models/B2bService/Contract/Invoice/list-b-2-b-invoice-status-response';
import { CustomRxState } from '@core/state/rx.state';
import { ListB2BInvoiceResponse } from '@api/models/B2bService/Contract/Invoice/list-b-2-b-invoice-response';
import { B2bInvoiceFilterBody } from '../models/b2b-filter-invoice.model';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { AddB2BInvoiceRequest } from '@api/models/B2bService/Contract/Invoice/add-b-2-b-invoice-request';
import { SetB2BInvoiceRequest } from '@api/models/B2bService/Contract/Invoice/set-b-2-b-invoice-request';
import { tap } from 'rxjs/operators';


interface IB2bProgramInvoicesState {
    loading: Record<StartWithUnit, boolean>;
    statuses: ListB2BInvoiceStatusResponse[];
    invoices: ListB2BInvoiceResponse[];
    invoicesFilter: B2bInvoiceFilterBody;
}

interface IB2bProgramInvoicesAction {
    requestInvoices: ListB2BInvoiceStatusResponse[];
    setInvoicesFilter: B2bInvoiceFilterBody;
}

@Injectable({
    providedIn: 'root',
})
export class B2bProgramInvoicesState extends CustomRxState<IB2bProgramInvoicesState> {
    readonly #invoiceService: InvoiceService = inject(InvoiceService);
    readonly #actions: RxActions<IB2bProgramInvoicesAction> = new RxActionFactory<IB2bProgramInvoicesAction>().create();

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly statuses$: Observable<ListB2BInvoiceStatusResponse[]> = this.selectLazy('statuses', this.requestStatuses);
    public readonly invoices$: Observable<ListB2BInvoiceResponse[]> = this.select('invoices');

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();
    }

    public requestInvoices(body: B2bInvoiceFilterBody): void {
        this.#actions.setInvoicesFilter(body);

        this.hold(
            this.#invoiceService.apiB2BListB2BInvoiceGet(body).pipe(
                startWithLoading(this, StartWithUnit.RequestB2BProgramInvoices),
            ),
            this.#actions.requestInvoices,
        );

    }

    public createInvoice(body: AddB2BInvoiceRequest): Observable<unknown> {
        return this.#invoiceService.apiB2BAddB2BInvoicePost({ body }).pipe(
            startWithLoading(this, StartWithUnit.CreateB2BProgramInvoice),
            tap(() => this.requestInvoices(this.get('invoicesFilter'))),
        );
    }

    public deleteInvoice(invId: number): Observable<unknown> {
        return this.#invoiceService.apiB2BRemoveB2BInvoiceDelete({ invId }).pipe(
            startWithLoading(this, StartWithUnit.DeleteB2BProgramInvoice),
            tap(() => this.requestInvoices(this.get('invoicesFilter'))),
        );
    }

    public changeInvoiceStatus(body: SetB2BInvoiceRequest): Observable<unknown> {
        return this.#invoiceService.apiB2BSetB2BInvoicePost({ body }).pipe(
            startWithLoading(this, StartWithUnit.SetB2BProgramInvoiceStatus),
            tap(() => this.requestInvoices(this.get('invoicesFilter'))),
        );
    }

    private requestStatuses(): Observable<ListB2BInvoiceStatusResponse[]> {
        return this.#invoiceService.apiB2BListB2BInvoiceStatusGet();
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            invoices: null,
            invoicesFilter: null,
        });
    }

    private connectSelectors(): void {
        this.connect('invoices', this.#actions.requestInvoices$);
        this.connect('invoicesFilter', this.#actions.setInvoicesFilter$);
    }
}
